import { useApi, PaginatedResponse } from '@/api';
import { Asset } from '@/components';
import {
  TableSkeleton,
  PercentageValue,
  DataTable,
  Flex,
  Heading
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import { useTableProps } from '@blockanalitica/ui';
import { useParams } from 'react-router';
import { Link } from '@/components';

type IRMParamsData = {
  underlying_address: string;
  underlying_name: string;
  underlying_symbol: string;
  base_rate: string;
  slope_rate_1: string;
  slope_rate_2: string;
  u_optimal: string;
};

export default function MarketIRMParamsTable() {
  const { network } = useParams() as { network: string };

  const { sorting } = useTableProps({
    pageSize: 15,
    initialSorting: '-base_rate'
  });
  const [data, error] = useApi<PaginatedResponse<IRMParamsData[]>>({
    url: '/params/irm/',
    queryParams: {
      sort: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn<IRMParamsData>[] = [
    {
      header: 'Market',
      getCell: (row) => (
        <Link to={`/markets/${row.underlying_address}`}>
          <Asset
            symbol={row.underlying_symbol}
            address={row.underlying_address}
            network={network}
            name={row.underlying_name}
            iconSize="3"
          />
        </Link>
      )
    },
    {
      header: 'Base Rate',
      getCell: (row) => <PercentageValue value={row.base_rate} />,
      sortingField: 'base_rate',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Variable Slope 1',
      getCell: (row) => <PercentageValue value={row.slope_rate_1} />,
      sortingField: 'slope_rate_1',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Variable Slope 2',
      getCell: (row) => <PercentageValue value={row.slope_rate_2} />,
      sortingField: 'slope_rate_2',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Optimal Utilization',
      getCell: (row) => <PercentageValue value={row.u_optimal} />,
      sortingField: 'u_optimal',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <Flex flexDirection="column" gap="s">
      <Heading size="3">Interest Rate Strategy Parameters</Heading>
      <DataTable
        rows={data.results}
        columns={columns}
        getKey={(row) => row.underlying_address}
        sorting={sorting}
      />
    </Flex>
  );
}
